import * as React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import { ApiHelper } from '../../../ApiHelper';
import { AgentTaskFlameGraphNode, AgentTaskFlameGraphResult, buildFlameGraphTree } from '../shared';
import { FlameGraphCustom } from '../../FlameGraphCustom.tsx';

export function AgentTaskFlameGraph(props: {
    serviceName: string;
    taskListId: number;
    taskId: number;
    snapshotId?: number;
    metricId?: number | null;
}) {
    const { serviceName, taskListId, taskId, snapshotId, metricId } = props;
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [rootNode, setRootNode] = React.useState<AgentTaskFlameGraphNode | null>(null);

    React.useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const query = snapshotId !== undefined ? `?snapshotId=${snapshotId}` : '';
        ApiHelper(`/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/flamegraph${query}`)
            .then((response: AgentTaskFlameGraphResult) => {
                if (!isMounted) {
                    return;
                }

                if (response.error) {
                    setError(response.error);
                    setRootNode(null);
                    setLoading(false);
                    return;
                }

                if (!response.nodes || response.nodes.length === 0) {
                    setRootNode(null);
                    setLoading(false);
                    return;
                }

                setRootNode(buildFlameGraphTree(response));
                setLoading(false);
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setError(err.message || 'Failed to load flame graph.');
                setRootNode(null);
                setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [serviceName, taskListId, snapshotId, taskId]);

    if (loading) {
        return <Spinner size={SpinnerSize.medium} label="Loading flame graph..." />;
    }

    if (error) {
        return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
    }

    if (!rootNode) {
        return (
            <MessageBar messageBarType={MessageBarType.info}>
                No flame graph is available for this task and snapshot yet.
            </MessageBar>
        );
    }

    return (
        <div style={{ border: '1px solid #e1dfdd', borderRadius: 8, padding: 8, height: 380 }}>
            <AutoSizer>
                {({ width, height }) => (
                    <FlameGraphCustom
                        data={rootNode}
                        width={Math.max(width, 300)}
                        height={Math.max(height, 300)}
                        theme="minimal"
                        metric={metricId ?? 0}
                        metricContext="commit"
                        fontSize={12}
                    />
                )}
            </AutoSizer>
        </div>
    );
}

